import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Restaurant } from '../types';

// Helper component to center map on user location
const RecenterMap: React.FC<{ coords: { lat: number; lng: number } | undefined }> = ({ coords }) => {
    const map = useMap();
    const hasCenteredRef = React.useRef(false);

    React.useEffect(() => {
        // Ensure coords are valid before setting view
        if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number' && !hasCenteredRef.current) {
            map.setView([coords.lat, coords.lng], 15, {
                animate: true,
                duration: 1
            });
            hasCenteredRef.current = true;
        }
    }, [coords, map]);

    return null;
};

// Custom marker icon creator
const createCustomIcon = (restaurant: Restaurant) => {
    return L.divIcon({
        className: 'custom-restaurant-marker',
        html: `
            <div class="relative flex flex-col items-center group cursor-pointer" style="width: 100px; height: 100px;">
                <!-- Main Circle Image -->
                <div class="w-16 h-16 rounded-full border-[3px] border-white shadow-2xl overflow-hidden bg-gray-200 flex-shrink-0 relative z-10">
                    <img 
                        src="${restaurant.image}" 
                        class="w-full h-full object-cover block" 
                        style="width: 100%; height: 100%; object-fit: cover;"
                        onerror="this.src='https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=200'"
                    />
                </div>
                
                <!-- Top Right Rating Badge -->
                <div class="absolute top-1 right-3 bg-yellow-400 text-black text-[10px] w-6 h-6 flex items-center justify-center rounded-full border-2 border-white font-black shadow-lg z-20">
                    ${restaurant.rating}
                </div>
                
                <!-- Bottom Name Badge -->
                <div class="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] px-3 py-1 rounded-full whitespace-nowrap font-black shadow-lg border border-white z-20 max-w-[110px] truncate">
                    ${restaurant.name}
                </div>
            </div>
        `,
        iconSize: [100, 100],
        iconAnchor: [50, 50],
    });
};

interface RestaurantMapProps {
    restaurants: Restaurant[];
    onViewMenu: (restaurantId: string) => void;
    userLocation?: { lat: number; lng: number };
}

const RestaurantMap: React.FC<RestaurantMapProps> = ({ restaurants, onViewMenu, userLocation }) => {
    // Filter restaurants with valid locations (extra safety)
    const restaurantsWithLocation = restaurants.filter(r => 
        r.location && 
        !isNaN(Number(r.location.lat)) && 
        !isNaN(Number(r.location.lng)) &&
        r.location.lat !== undefined &&
        r.location.lng !== undefined
    );

    // Initial center: user location (if valid), or first restaurant, or Algiers
    const isValidUserLocation = userLocation && 
                               typeof userLocation.lat === 'number' && 
                               typeof userLocation.lng === 'number' &&
                               !isNaN(userLocation.lat) &&
                               !isNaN(userLocation.lng);

    const initialCenter: [number, number] = isValidUserLocation
        ? [userLocation!.lat, userLocation!.lng]
        : restaurantsWithLocation.length > 0
            ? [restaurantsWithLocation[0].location!.lat, restaurantsWithLocation[0].location!.lng]
            : [36.7538, 3.0588]; // Algiers

    return (
        <div className="w-full h-[calc(100vh-180px)] rounded-3xl overflow-hidden shadow-2xl border border-white/50 relative mt-4">
            <style>
                {`
                .custom-restaurant-marker {
                    background: none;
                    border: none;
                }
                .leaflet-div-icon {
                    background: transparent;
                    border: none;
                }
                .user-location-marker {
                    background: #3b82f6;
                    border: 3px solid white;
                    border-radius: 50%;
                    box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
                }
                `}
            </style>
            <MapContainer
                center={initialCenter}
                zoom={13}
                className="w-full h-full z-0"
                zoomControl={false}
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                <RecenterMap coords={isValidUserLocation ? userLocation : undefined} />

                {/* User Location Marker */}
                {isValidUserLocation && (
                    <Marker
                        position={[userLocation!.lat, userLocation!.lng]}
                        icon={L.divIcon({
                            className: 'user-location-marker',
                            iconSize: [16, 16],
                            iconAnchor: [8, 8]
                        })}
                    >
                        <Popup>Votre position</Popup>
                    </Marker>
                )}

                {restaurantsWithLocation.map(restaurant => (
                    <Marker
                        key={restaurant.id}
                        position={[restaurant.location!.lat, restaurant.location!.lng]}
                        icon={createCustomIcon(restaurant)}
                        eventHandlers={{
                            click: () => onViewMenu(restaurant.id)
                        }}
                    />
                ))}
            </MapContainer>

            {restaurantsWithLocation.length === 0 && (
                <div className="absolute inset-x-4 bottom-4 z-[400] bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-2xl border border-white/50 text-center">
                    <p className="text-xs font-bold text-gray-500 italic">
                        Aucun restaurant avec une position GPS n'a été trouvé.
                    </p>
                </div>
            )}
        </div>
    );
};

export default RestaurantMap;
